/**
 * Copyright (c) 2025 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

// ============================================================================
// USER ROLES
// ============================================================================

export const UserRole = {
  UNAUTH: 'unauth',
  MEMBER: 'member',
  PREVIEW: 'preview',
  SUPPORTER: 'supporter',
  TRADESHOW: 'tradeshow',
  MODERATOR: 'moderator',
  ADMIN: 'admin',
} as const

export type UserRole = (typeof UserRole)[keyof typeof UserRole]

/** Role hierarchy for comparison (higher number = more privileges) */
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  [UserRole.UNAUTH]: 0,
  [UserRole.MEMBER]: 1,
  [UserRole.PREVIEW]: 2,
  [UserRole.SUPPORTER]: 3,
  [UserRole.TRADESHOW]: 4,
  [UserRole.MODERATOR]: 5,
  [UserRole.ADMIN]: 6,
}

// ============================================================================
// ROLE HIERARCHY UTILITIES
// ============================================================================

/** Check if roleA has higher or equal privileges than roleB */
export function hasHigherOrEqualRole(roleA: UserRole, roleB: UserRole): boolean {
  return ROLE_HIERARCHY[roleA] >= ROLE_HIERARCHY[roleB]
}

/** Check if roleA has higher privileges than roleB */
export function hasHigherRole(roleA: UserRole, roleB: UserRole): boolean {
  return ROLE_HIERARCHY[roleA] > ROLE_HIERARCHY[roleB]
}

/** Check if a role is at least at the specified minimum role level */
export function isAtLeastRole(role: UserRole, minimumRole: UserRole): boolean {
  return ROLE_HIERARCHY[role] >= ROLE_HIERARCHY[minimumRole]
}

/** Check if a string is a valid UserRole */
export function isValidRole(value: string): value is UserRole {
  return Object.values(UserRole).includes(value as UserRole)
}
